<spinner *ngIf="loading"></spinner>

<cdss-patient-detail-header
    *ngIf="!loading && patient"
    [patientId]="patientId"
    [patient]="patient"
    currentLabel="MEED Risk Summary"
    (goBack)="goBack()">
</cdss-patient-detail-header>

<div class="risk-panel position-relative" *ngIf="!loading">

    <!-- MEED HEADER -->

    <div class="panel-header">

        <div class="panel-header__title-row">
            <h2 class="panel-title">MEED Risk Summary</h2>
        </div>
        <!-- Identity details the shared header doesn't carry -->
        <div class="panel-meta" *ngIf="patient">
            {{ patientIdentifier }} | {{ patient?.diagnosis ? patient?.diagnosis : '-' }}
            <ng-container *ngIf="patient?.biologicalSex || patient?.age"> | {{ patient?.biologicalSex }}, {{ patient?.age }}</ng-container>
        </div>

        <!-- Rolling section -->

        <div class="d-flex justify-content-between align-items-center mt-3">

            <div class="d-flex align-items-center gap-2 text-muted">
                <button type="button" class="week-nav-arrow" (click)="previousWeek()" title="Previous week">
                    <i class="bi bi-chevron-left"></i>
                </button>
                <span>
                    <strong>Rolling 7 days</strong> {{ dateRangeLabel }}
                </span>
                <button type="button" class="week-nav-arrow" (click)="nextWeek()" title="Next week">
                    <i class="bi bi-chevron-right"></i>
                </button>
            </div>

            <!-- Action buttons -->
            <div *ngIf="canTakeAction" class="d-flex gap-2">
                <span *ngIf="!hasRiskRecord" class="text-muted smaller align-self-center">
                    Today's risk hasn't been calculated yet
                </span>
                <button
                    class="btn no-action px-3 smaller"
                    [disabled]="!hasRiskRecord"
                    (click)="onActionClick('No action needed')">
                    NO ACTION NEEDED
                </button>
                <button
                    class="btn risk-assessment px-3 smaller"
                    [disabled]="!hasRiskRecord"
                    (click)="onActionClick('Risk assessment updated')">
                    RISK ASSESSMENT UPDATED
                </button>
            </div>

            <div *ngIf="!canTakeAction" class="d-flex align-items-center gap-2 text-muted">
                <span class="fw-semibold d-flex align-items-center gap-2">
                    Action Logged:
                    <span class="badge rounded-pill bg-light text-dark border d-flex align-items-center gap-1">
                        <i class="bi bi-check2 text-success"></i>
                        {{ lastActionType | uppercase }}
                    </span>
                    at {{ lastActionDate | date: 'HH:mm' }}
                </span>
            </div>
        </div>

    </div>

    <!-- Risk Transition -->

    <div class="card border-0 mb-4" *ngIf="currentMeedRisk">
        <div class="card-body p-4">
            <h5 class="fw-semibold mb-5 text-dark" style="font-size: 16px; opacity: 0.8;">
                Risk transition (7-day comparison)
            </h5>

            <div class="d-flex align-items-center justify-content-between px-5 position-relative">
                <!-- Connecting Line -->
                <div class="position-absolute top-50 start-50 translate-middle w-75 border-bottom"
                     style="z-index: 1; border-color: #dee2e6 !important;"></div>

                <!-- Previous -->
                <div class="text-center position-relative" style="z-index: 2; width: 250px;">
                    <div class="text-muted mb-2 small fw-bold text-gray">Previous 7 days</div>
                    <div class="risk-pill d-inline-flex align-items-center gap-2"
                         [ngClass]="isUnknownRisk(currentMeedRisk.previousRisk) ? 'meed-unknown-badge' : 'risk-' + (currentMeedRisk.previousRisk || '').toLowerCase()"
                         [attr.title]="isUnknownRisk(currentMeedRisk.previousRisk) ? unknownRiskTooltip : null">
                        <i class="bi me-1" [ngClass]="isUnknownRisk(currentMeedRisk.previousRisk) ? 'bi-exclamation-triangle-fill' : 'bi-exclamation-triangle'" aria-hidden="true"></i>
                        {{ currentMeedRisk.previousRisk || 'Unknown' }} Risk
                    </div>
                    <div class="mt-3 text-muted small text-gray">
                        <strong>Trigger: {{ currentMeedRisk.previousTrigger }}</strong>
                    </div>
                </div>

                <!-- Trend -->
                <div class="text-center position-relative" style="z-index: 2;">
                    <div class="trend-pill d-inline-flex align-items-center gap-2 px-4 py-2"
                         [ngClass]="'trend-' + (currentMeedRisk.trend || 'stable').toLowerCase()">
                        {{ currentMeedRisk.trend || 'Unknown' }}
                        <i class="bi" [ngClass]="{
                            'bi-arrow-up-right': currentMeedRisk.trend === 'Improving',
                            'bi-arrow-down-right': currentMeedRisk.trend === 'Deteriorating',
                            'bi-arrow-right': currentMeedRisk.trend === 'Stable'
                        }"></i>
                    </div>
                </div>

                <!-- Current -->
                <div class="text-center position-relative" style="z-index: 2; width: 250px;">
                    <div class="text-muted mb-2 small fw-bold">Current 7 days</div>
                    <div class="risk-pill d-inline-flex align-items-center gap-2 shadow-sm"
                         [ngClass]="isUnknownRisk(currentMeedRisk.currentRisk) ? 'meed-unknown-badge' : 'risk-' + (currentMeedRisk.currentRisk || '').toLowerCase()"
                         [attr.title]="isUnknownRisk(currentMeedRisk.currentRisk) ? unknownRiskTooltip : null">
                        <i class="bi me-1" [ngClass]="isUnknownRisk(currentMeedRisk.currentRisk) ? 'bi-exclamation-triangle-fill' : 'bi-exclamation-triangle'" aria-hidden="true"></i>
                        {{ currentMeedRisk.currentRisk || 'Unknown' }} Risk
                    </div>
                    <div class="mt-3 text-muted small">
                        <strong>Trigger: {{ currentMeedRisk.currentTrigger }}</strong>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Behavioural Breakdown Panel -->
    <div class="card mb-4 border-0">
        <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-4">
                <h5 class="fw-semibold mb-0">Behavioural breakdown</h5>
                <div class="d-flex align-items-center gap-3">
                    <div class="d-flex align-items-center gap-2 small bg-patient-active px-2 rounded">
                        <span class="dot patient-dot"></span> Patient reported
                    </div>
                    <div class="d-flex align-items-center gap-2 small bg-carer-active px-2 rounded">
                        <span class="dot carer-dot"></span> Carer reported
                    </div>
                </div>
            </div>

            <div class="table-responsive">
                <overlay-spinner *ngIf="refreshing"></overlay-spinner>
                <table class="table table-borderless align-middle">
                    <thead>
                        <tr class="text-uppercase text-muted small">
                            <th style="width: 250px">Risk Factor</th>
                            <th *ngFor="let date of dates" style="width: 60px" class="text-center">{{ date }}</th>
                            <th class="text-center" style="width: 250px">Diff</th>
                            <th class="text-center">Meed Risk Rating</th>
                        </tr>
                    </thead>
                    <tbody>
                    <ng-container *ngFor="let factor of factorTableData">
                        <tr>
                            <td class="border-bottom">
                                <div class="risk-factor-top-text">{{ factor.name }}</div>
                                <div class="text-muted smaller">{{ factor.unit }}</div>
                            </td>
                            <td *ngFor="let day of factor.daily; let i = index" class="text-center border-bottom">
                                <div class="d-flex flex-column gap-1 align-items-center">
                                    <div class="value-circle bg-patient-active"
                                         [ngClass]="{'bg-exercise-active': day.C < day.P}">
                                        <span class="type-label">{{ i === 0 ? 'P' : '' }}</span> {{ day.P }}
                                    </div>
                                    <div class="value-circle bg-carer-active"
                                         [ngClass]="{'bg-exercise-active': day.C > day.P}">
                                        <span class="type-label">{{ i === 0 ? 'C' : '' }}</span> {{ day.C }}
                                    </div>
                                </div>
                            </td>
                            <td class="text-center">
                                <div class="diff-pill" style="width: 80px" [ngClass]="'diff-' + factor.diff.color">
                                    <img *ngIf="factor.diff.value > 0" src="assets/images/cdss/patient-meed-risk/trend-line-up.svg" alt="trend-line-up">
                                    <img *ngIf="factor.diff.value < 0" src="assets/images/cdss/patient-meed-risk/trend-line-down.svg" alt="trend-line-down">
                                    <img *ngIf="factor.diff.value === 0" src="assets/images/cdss/patient-meed-risk/hyphen.svg" alt="hyphen">
                                    {{ factor.diff.value === 0 ? '0' : factor.diff.value > 0 ? '+' + factor.diff.value : factor.diff.value }}
                                </div>
                            </td>
                            <td>
                                <div class="d-flex justify-content-center">
                                    <div style="width: 200px" class="risk-rating-badge"
                                         [ngClass]="isUnknownRisk(factor.risk.color) ? 'meed-unknown-badge' : 'risk-rating-' + factor.risk.color"
                                         [attr.title]="isUnknownRisk(factor.risk.color) ? unknownRiskTooltip : null">
                                        <i class="bi me-1" [ngClass]="isUnknownRisk(factor.risk.color) ? 'bi-exclamation-triangle-fill' : 'bi-exclamation-triangle'" aria-hidden="true"></i>
                                        {{ factor.risk.label }}
                                    </div>
                                </div>
                                <div class="smaller text-muted mt-1 text-center">
                                    {{ factor.risk.sub }}
                                </div>
                            </td>
                        </tr>
                    </ng-container>
                    </tbody>
                </table>
            </div>

            <div class="text-center text-muted smaller mt-3">
                Note: Risk Rating is determined by the highest reported value (Patient or Carer) to ensure safety.
            </div>
        </div>
    </div>


    <!-- Risk Assessment Log Panel -->
    <div class="mt-5">
        <div class="d-flex align-items-center gap-2 mb-3 cursor-pointer" (click)="showHistory = !showHistory">
            <i class="bi" [ngClass]="showHistory ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            <span class="head-color fw-semibold">Show history of risk ({{ logsTotalCount }})</span>
        </div>

        <div class="card border-0" *ngIf="showHistory">
            <div class="card-body p-0">
                <table class="table mb-0 align-middle">
                    <thead class="bg-light">
                    <tr class="text-uppercase text-muted smaller">
                        <th class="ps-4 head-color">Timestamp</th>
                        <th class="head-color px-5">Clinician</th>
                        <th class="head-color text-start">Content</th>
                        <th class="text-center">Action Taken</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr *ngFor="let log of assessmentLogs">
                        <td class="ps-4 text-muted smaller">
                            {{ log.createdAt | date: 'dd MMM, HH:mm' }}
                        </td>
                        <td class="fw-semibold smaller px-5">
                            {{ log.createdBy.firstName ?? "" + " " + log.createdBy.lastName ?? "" }}
                        </td>
                        <td class="text-start smaller">
                            {{ 'Transitioning from ' + (currentMeedRisk?.previousRisk ?? 'unknown') +' to ' + (currentMeedRisk?.currentRisk ?? 'unknown') }}
                        </td>
                        <td class="text-center pe-4">
                                <span
                                    class="alert-type-style badge d-flex align-items-center justify-content-center gap-1 py-2 px-3 mx-auto"
                                    [ngClass]="log.actionTaken === 'No action needed' ? 'no-action' : 'risk-assessment'"
                                >
                                    <i *ngIf="log.actionTaken !== 'No action needed'" class="bi bi-check2 fs-6"></i>
                                    {{ log.actionTaken?.toUpperCase() }}
                                </span>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination controls -->
            <div class="d-flex justify-content-between align-items-center px-3 py-3 border-top" *ngIf="logsTotalPages > 1">
                <span class="text-muted smaller">
                    Page {{ logsCurrentPage }} of {{ logsTotalPages }}
                </span>
                <div class="d-flex gap-2">
                    <button class="btn btn-sm btn-outline-secondary"
                            [disabled]="logsCurrentPage === 1"
                            (click)="logsPrevPage()">
                        ‹ Prev
                    </button>
                    <button class="btn btn-sm btn-outline-secondary"
                            [disabled]="logsCurrentPage === logsTotalPages"
                            (click)="logsNextPage()">
                        Next ›
                    </button>
                </div>
            </div>

        </div>
    </div>
</div>
